import { BookingStatus } from '../models/BookingStatus';
import { PaymentActivityType } from '../models/PaymentActivityType';
import { RecurrenceType } from '../models/RecurrenceType';
import { getDisplayLocale } from '../../utils/timezoneUtils';
import {
  formatClockTime as formatLocalizedClockTime,
  formatDuration,
  formatWeekdayCode,
} from '../../utils/displayFormat';

/** A t-function shape compatible with react-i18next without importing its types here. */
type Translate = (key: string) => string;

/**
 * Maps BookingStatus enum to its label in the UI language
 * @param status - The BookingStatus enum value
 * @param t - The active translation function
 * @returns Display string for the status
 */
export const mapBookingStatusToDisplay = (status: BookingStatus, t: Translate): string => {
  switch (status) {
    case BookingStatus.Active:
      return t('sessions.status.active');
    case BookingStatus.Cancelled:
      return t('sessions.status.cancelled');
    default:
      return status;
  }
};

/** The session form's own label for each recurrence, so the details page repeats what was picked. */
const RECURRENCE_LABEL_KEYS: Record<RecurrenceType, string> = {
  [RecurrenceType.No]: 'sessionForm.doesNotRepeat',
  [RecurrenceType.Daily]: 'sessionForm.daily',
  [RecurrenceType.DailyWorkdays]: 'sessionForm.everyWeekday',
  [RecurrenceType.DailyWeekend]: 'sessionForm.everyWeekendDay',
  [RecurrenceType.Weekly]: 'sessionForm.weekly',
  [RecurrenceType.BiWeekly]: 'sessionForm.everyTwoWeeks',
  [RecurrenceType.EveryThirdWeek]: 'sessionForm.everyThreeWeeks',
  [RecurrenceType.EveryFourthWeek]: 'sessionForm.everyFourWeeks',
  [RecurrenceType.MonthlyDayNumber]: 'sessionForm.monthly',
  [RecurrenceType.MonthlyFirstDay]: 'sessionForm.firstWeekdayOfMonth',
};

/**
 * Maps PaymentActivityType enum to its label in the UI language
 * @param type - The PaymentActivityType enum value
 * @param t - The active translation function
 * @returns Display string for the payment type
 */
export const mapPaymentActivityTypeToDisplay = (type: PaymentActivityType, t: Translate): string =>
  Object.values(PaymentActivityType).includes(type) ? t(`sessions.paymentActivityType.${type}`) : type;

/**
 * Maps RecurrenceType enum to its label in the UI language
 * @param type - The RecurrenceType enum value
 * @param t - The active translation function
 * @returns Display string for the recurrence type
 */
export const mapRecurrenceTypeToDisplay = (type: RecurrenceType, t: Translate): string => {
  const key = RECURRENCE_LABEL_KEYS[type];
  return key ? t(key) : type;
};

/**
 * Maps wire weekday codes ("MON", "FRI") to the UI language's short weekday names
 * @param days - Weekday codes from the session
 * @returns Comma-separated weekday names
 */
export const formatRecurrenceDaysDisplay = (days: string[]): string =>
  days.map((day) => formatWeekdayCode(day, getDisplayLocale())).join(', ');

/**
 * Formats a HH:mm local time for display in the active UI locale
 * (e.g. "6:00 PM" for en-US, "18:00" for de-DE)
 * @param timeLocal - Time in HH:mm format
 * @returns Formatted time string
 */
export const formatTimeDisplay = (timeLocal: string): string => {
  const [hours, minutes] = timeLocal.split(':').map(Number);
  return formatClockTime(hours, minutes);
};

/**
 * Calculates end time based on start time and duration
 * @param timeLocal - Start time in HH:mm format
 * @param duration - Duration in minutes
 * @returns End time formatted in the active UI locale
 */
export const calculateEndTimeDisplay = (timeLocal: string, duration: number): string => {
  const [hours, minutes] = timeLocal.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + duration;
  return formatClockTime(Math.floor(totalMinutes / 60) % 24, totalMinutes % 60);
};

const formatClockTime = (hours: number, minutes: number): string =>
  formatLocalizedClockTime(hours, minutes, getDisplayLocale());

/**
 * Formats date to long format
 * @param date - Date object
 * @returns Formatted date string
 */
export const formatDateDisplay = (date: Date | string): string => {
  return new Intl.DateTimeFormat(getDisplayLocale(), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
};

/**
 * Formats duration in minutes in the UI language ("1 Stunde, 30 Minuten" in de)
 * @param minutes - Duration in minutes
 * @returns Formatted duration string
 */
export const formatDurationDisplay = (minutes: number): string =>
  formatDuration(minutes, getDisplayLocale());
